/** The views of the growth screens, as the address bar holds them. A default is not written. */
import { instantIn, MARKET_TIME_ZONE } from "@/shared/lib/format";
import { idOf } from "@/shared/lib/search";

import type { CampaignFilter, PromoFilter, SmsFilter } from "../api/queries";
import {
  CAMPAIGN_KINDS,
  CAMPAIGN_STATUSES,
  SIDES,
  type CampaignKind,
  type CampaignStatus,
  type Side,
} from "../api/types";

function text(value: unknown): string | undefined {
  const string = typeof value === "number" ? String(value) : value;
  return typeof string === "string" && string.trim() !== "" ? string.trim() : undefined;
}

function pageOf(value: unknown): number | undefined {
  const page = Number(value);
  return Number.isInteger(page) && page > 1 ? page : undefined;
}

/** Which codes the list shows: the live ones, those switched off, or the deleted. */
export const PROMO_SHOWN = ["active", "off", "deleted"] as const;
export type PromoShown = (typeof PROMO_SHOWN)[number];

export interface PromoSearch {
  q?: string;
  show?: PromoShown;
  /** Only this referral partner's codes: their user id. */
  partner?: string;
  page?: number;
}

export function parsePromoSearch(raw: Readonly<Record<string, unknown>>): PromoSearch {
  const search: PromoSearch = {};
  const q = text(raw["q"]);
  if (q !== undefined) search.q = q;
  const show = PROMO_SHOWN.find((known) => known === raw["show"]);
  if (show !== undefined) search.show = show;
  const partner = idOf(raw["partner"]);
  if (partner !== undefined) search.partner = partner;
  const page = pageOf(raw["page"]);
  if (page !== undefined) search.page = page;
  return search;
}

export function promoFilter(search: PromoSearch): PromoFilter {
  const filter: PromoFilter = {};
  if (search.q !== undefined) filter.q = search.q;
  if (search.partner !== undefined) filter.partner_user_id = search.partner;
  if (search.show === "active") filter.is_active = true;
  if (search.show === "off") filter.is_active = false;
  if (search.show === "deleted") filter.deleted = true;
  return filter;
}

export interface CampaignSearch {
  status?: CampaignStatus;
  kind?: CampaignKind;
  side?: Side;
  page?: number;
}

export function parseCampaignSearch(raw: Readonly<Record<string, unknown>>): CampaignSearch {
  const search: CampaignSearch = {};
  const status = CAMPAIGN_STATUSES.find((known) => known === raw["status"]);
  if (status !== undefined) search.status = status;
  const kind = CAMPAIGN_KINDS.find((known) => known === raw["kind"]);
  if (kind !== undefined) search.kind = kind;
  const side = SIDES.find((known) => known === raw["side"]);
  if (side !== undefined) search.side = side;
  const page = pageOf(raw["page"]);
  if (page !== undefined) search.page = page;
  return search;
}

export function campaignFilter({ page: _, ...filter }: CampaignSearch): CampaignFilter {
  return filter;
}

/** What v1 wrote of an SMS: whether it went, and what it was. */
export const SMS_STATUSES = ["sent", "failed"] as const;
export const SMS_TYPES = ["otp", "notification"] as const;

/**
 * The log's view. The number it is looked up by is not part of it: a phone
 * is personal data, kept out of the address (`@/shared/lib/private-search`).
 */
export interface SmsSearch {
  status?: (typeof SMS_STATUSES)[number];
  type?: (typeof SMS_TYPES)[number];
  /** The first day, `YYYY-MM-DD`, by the market's clock. */
  from?: string;
  /** The last day, the whole of it. */
  to?: string;
}

/** A day as a date field writes it; anything else is not a day. */
function dayOf(value: unknown): string | undefined {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const [year, month, day] = value.split("-").map(Number) as [number, number, number];
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCMonth() === month - 1 && date.getUTCDate() === day ? value : undefined;
}

export function parseSmsSearch(raw: Readonly<Record<string, unknown>>): SmsSearch {
  const search: SmsSearch = {};
  const status = SMS_STATUSES.find((known) => known === raw["status"]);
  if (status !== undefined) search.status = status;
  const type = SMS_TYPES.find((known) => known === raw["type"]);
  if (type !== undefined) search.type = type;
  const from = dayOf(raw["from"]);
  if (from !== undefined) search.from = from;
  const to = dayOf(raw["to"]);
  if (to !== undefined) search.to = to;
  return search;
}

/** The day after a day: where "the whole of `to`" ends. */
function dayAfter(day: string): string {
  const [year, month, date] = day.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(year, month - 1, date + 1)).toISOString().slice(0, 10);
}

/** The fewest digits of a number hg-notify looks a message up by. */
export const PHONE_DIGITS = 4;

/**
 * The filter as it is asked. A number of fewer than four digits is not
 * asked yet: the service would refuse it, and the person is still typing.
 * Days are the market's: from the start of the first to the start of the
 * day after the last (`created_to` is "before this moment").
 */
export function smsFilter(search: SmsSearch, phone?: string): SmsFilter {
  const filter: SmsFilter = {};
  const digits = phone?.replace(/\D/g, "") ?? "";
  if (digits.length >= PHONE_DIGITS) filter.phone = digits;
  if (search.status !== undefined) filter.status = search.status;
  if (search.type !== undefined) filter.type = search.type;
  const from =
    search.from === undefined ? null : instantIn(`${search.from}T00:00`, MARKET_TIME_ZONE);
  if (from !== null) filter.created_from = from;
  const to =
    search.to === undefined ? null : instantIn(`${dayAfter(search.to)}T00:00`, MARKET_TIME_ZONE);
  if (to !== null) filter.created_to = to;
  return filter;
}

/** Which of a person's messages: one app's, or all. */
export interface SentSearch {
  side?: Side;
}

export function parseSentSearch(raw: Readonly<Record<string, unknown>>): SentSearch {
  const side = SIDES.find((known) => known === raw["side"]);
  return side === undefined ? {} : { side };
}

// ---- referral partners ----

/** The partners' list: its page. What it is searched for names a person, and is not here. */
export interface PartnersSearch {
  page?: number;
}

export function parsePartnersSearch(raw: Readonly<Record<string, unknown>>): PartnersSearch {
  const page = pageOf(raw["page"]);
  return page === undefined ? {} : { page };
}
